import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Image from 'next/image';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import EastIcon from '@mui/icons-material/East';
import {cdnImage} from '@/util/cdnImage';
import {borderRadius, colors, fontSize, fontWeight, shadows, spacing} from '@/styles/tokens';
import {imgSizes} from '@/util/imageSizes';

const awardAI = cdnImage('about-us-new/AI2024.webp');
const clutch = cdnImage('about-us-new/Clutch-award.webp');
const goodfirms = cdnImage('about-us-new/Debut-award.webp');
const web = cdnImage('about-us-new/Web-development-award.webp');
const blockchain = cdnImage('about-us-new/Blockchain-award.webp');
const awardsList = [
    {image: awardAI, label: 'Top Artificial Intelligence Company in United States in 2024'},
    {image: goodfirms, label: 'Debut Infotech is a 2025 Award-Winning Company for its Reactjs Services'},
    {image: clutch, label: 'Top Clutch Blockchain Company United Kingdom 2026'},
    {image: web, label: 'Top Techreviewer Web Development Companies 2026'},
    {image: blockchain, label: 'Top Blockchain Development Companies 2026'}
];

const SubCard = ({image, label, showLabel = true}: {image: any; label: string; showLabel?: boolean}) => (
    <Box
        sx={{
            border: `1.5px solid ${colors.neutral.borderLight}`,
            borderTop: `3px solid ${colors.accent.blue500}`,
            borderRadius: borderRadius.lg,
            overflow: 'hidden',
            backgroundColor: colors.background.default
        }}
    >
        <Box sx={{position: 'relative', height: 200, backgroundColor: colors.background.slate50}}>
            {/* Grid is 1 col (xs) / 2 col (sm) / 5 col (md+, ~210px inside the 1200px container). */}
            <Image
                src={image}
                alt={label}
                fill
                sizes={imgSizes({xs: '90vw', sm: '45vw', md: '240px'})}
                style={{objectFit: 'contain', padding: spacing[20]}}
            />
        </Box>
        {showLabel && (
            <Box sx={{p: 2, borderTop: `1px solid ${colors.neutral.borderLight}`}}>
                <Typography className="font-headers" sx={{fontSize: fontSize[16], fontWeight: fontWeight.bold, color: colors.text.primary}}>
                    {label}
                </Typography>
            </Box>
        )}
    </Box>
);

const CardHeader = ({description}: {description: string}) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: {xs: 'column', sm: 'row'},
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
            mb: 4
        }}
    >
        <Box maxWidth={760}>
            <Typography className="font-regular-text" sx={{fontSize: fontSize[16], color: colors.text.primary, lineHeight: 1.7}}>
                {description}
            </Typography>
        </Box>
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                backgroundColor: colors.background.tag,
                gap: 1,
                border: `1px solid ${colors.neutral.slate300}`,
                borderRadius: borderRadius.full,
                px: 2.5,
                py: 1,
                cursor: 'pointer',
                flexShrink: 0,
                color: colors.text.primary
            }}
        >
            <EastIcon sx={{fontSize: fontSize[16]}} />
            <Typography className="font-text-regular" sx={{fontSize: fontSize[16]}}>
                Recognition across 5 global directories
            </Typography>
        </Box>
    </Box>
);

const Awards = () => {
    return (
        <Box component="section">
            <MUIContainer>
                {/* Section Heading */}
                <Box textAlign="center" mb={{xs: 5, md: 7}} maxWidth={760} mx="auto">
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            border: `1px solid ${colors.neutral.slate300}`,
                            borderRadius: borderRadius.full,
                            px: 2,
                            py: 0.75,
                            mb: 3,
                            backgroundColor: colors.background.default
                        }}
                    >
                        <VerifiedOutlinedIcon sx={{fontSize: fontSize[16], color: colors.status.success}} />
                        <Typography sx={{fontSize: fontSize[16], color: colors.text.muted, fontWeight: fontWeight.medium}}>
                            Trusted by enterprise teams worldwide
                        </Typography>
                    </Box>

                    <Box textAlign="center" maxWidth={1024} margin="0 auto" mb={5}>
                        <Typography
                            variant="h1"
                            component="h2"
                            mb={3}
                            fontSize={{xs: fontSize[32], sm: fontSize[48]}}
                            className="font-headers"
                        >
                            Awards & Recognitions That Speak for Our Work
                        </Typography>
                        <Typography variant="subtitle1" component="p" className="font-regular-text"></Typography>
                    </Box>
                </Box>

                {/* Awards Card — blue border to differentiate */}
                <Box
                    sx={{
                        backgroundColor: colors.background.default,
                        borderRadius: borderRadius['2xl'],
                        p: {xs: 3, md: 5},
                        boxShadow: shadows.lg,
                        border: `1.5px solid ${colors.neutral.borderLight}`
                    }}
                >
                    <CardHeader description="Independent platforms and analyst communities continue to rank our delivery, product strategy, and engineering quality among the top software development partners." />
                    <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr', md: 'repeat(5, 1fr)'}, gap: 3}}>
                        {awardsList.map((award) => (
                            <SubCard key={award.label} image={award.image} label={award.label} showLabel={false} />
                        ))}
                    </Box>
                </Box>
            </MUIContainer>
        </Box>
    );
};

export default memo(Awards);
